/**
 * Status and Role Helper Utilities
 *
 * Provides consistent status colors, labels, and role handling across the application
 */

// ========================================
// Status Color Mapping
// ========================================

/**
 * Get color for a given status
 * @param {string} status - The status value
 * @param {Object} customMapping - Optional custom color mapping
 * @returns {string} MUI color variant
 *
 * @example
 * getStatusColor('active') // 'success'
 * getStatusColor('pending') // 'warning'
 */
export const getStatusColor = (status, customMapping = {}) => {
  const defaultMapping = {
    // Positive statuses
    active: 'success',
    approved: 'success',
    completed: 'success',
    paid: 'success',
    success: 'success',
    accepted: 'success',

    // Pending/In-progress statuses
    pending: 'warning',
    'in-progress': 'warning',
    in_progress: 'warning',
    'pending-approval': 'warning',
    pending_replacement: 'warning',
    'pending-replacement': 'warning',
    pending_admin: 'info',
    'pending-admin': 'info',
    processing: 'warning',
    submitted: 'warning',
    'on-hold': 'warning',
    on_hold: 'warning',

    // Negative statuses
    inactive: 'error',
    rejected: 'error',
    failed: 'error',
    cancelled: 'default',
    expired: 'error',
    error: 'error',
    replacement_declined: 'error',
    'replacement-declined': 'error',

    // Draft/Info statuses
    draft: 'info',
    info: 'info'
  };

  const mapping = { ...defaultMapping, ...customMapping };
  return mapping[status?.toLowerCase()] || 'default';
};

/**
 * Get the display label for an invoice status.
 *
 * Frontend-only relabeling: an invoice with the underlying status "paid"
 * is shown as "Remittance Advice" across the UI. The stored value stays "paid".
 *
 * @param {string} status - Invoice status value
 * @returns {string} Display label
 *
 * @example
 * getInvoiceStatusLabel('paid')      // 'Remittance Advice'
 * getInvoiceStatusLabel('submitted') // 'SUBMITTED'
 */
export const getInvoiceStatusLabel = (status) => {
  return getStatusLabel(status, { paid: 'Remittance Advice' });
};

/**
 * Get formatted label for a status
 * @param {string} status - The status value
 * @param {Object} customLabels - Optional custom label mapping
 * @returns {string} Formatted status label in UPPERCASE
 *
 * @example
 * getStatusLabel('in-progress') // 'IN PROGRESS'
 * getStatusLabel('pending_approval') // 'PENDING APPROVAL'
 */
export const getStatusLabel = (status, customLabels = {}) => {
  if (!status) return '';

  // Check custom labels first
  if (customLabels[status]) {
    return customLabels[status];
  }

  // Convert snake_case or kebab-case to UPPERCASE
  return status.replace(/[-_]/g, ' ').toUpperCase();
};

/**
 * Get role color for user roles
 * @param {string} role - The user role
 * @returns {string} MUI color variant
 *
 * @example
 * getRoleColor('admin') // 'error'
 * getRoleColor('vendor') // 'primary'
 */
export const getRoleColor = (role) => {
  const roleColors = {
    admin: 'error',
    vendor: 'primary',
    opex_vendor: 'info',
    client: 'secondary',
    subcontractor: 'success',
    user: 'default'
  };

  return roleColors[role] || 'default';
};

/**
 * Get formatted label for a role
 * @param {string} role - The role value
 * @returns {string} Formatted role label
 *
 * @example
 * getRoleLabel('opex_vendor') // 'OPEX Vendor'
 */
export const getRoleLabel = (role) => {
  const roleLabels = {
    opex_vendor: 'OPEX Vendor',
    admin: 'Admin',
    vendor: 'Vendor',
    client: 'Client',
    subcontractor: 'Subcontractor'
  };

  return roleLabels[role] || getStatusLabel(role);
};

/**
 * Check if a status is considered "active" or "positive"
 * @param {string} status - The status value
 * @returns {boolean} True if status is active/positive
 *
 * @example
 * isActiveStatus('active') // true
 * isActiveStatus('approved') // true
 * isActiveStatus('rejected') // false
 */
export const isActiveStatus = (status) => {
  const activeStatuses = ['active', 'approved', 'completed', 'paid', 'success'];
  return activeStatuses.includes(status?.toLowerCase());
};

/**
 * Check if a status is considered "pending" or "in progress"
 * @param {string} status - The status value
 * @returns {boolean} True if status is pending
 *
 * @example
 * isPendingStatus('pending') // true
 * isPendingStatus('in-progress') // true
 */
export const isPendingStatus = (status) => {
  const pendingStatuses = ['pending', 'in-progress', 'pending-approval', 'processing', 'draft'];
  return pendingStatuses.includes(status?.toLowerCase());
};

/**
 * Check if a status is considered "negative" or "failed"
 * @param {string} status - The status value
 * @returns {boolean} True if status is negative
 *
 * @example
 * isNegativeStatus('rejected') // true
 * isNegativeStatus('failed') // true
 */
export const isNegativeStatus = (status) => {
  const negativeStatuses = ['rejected', 'failed', 'cancelled', 'expired', 'error', 'inactive'];
  return negativeStatuses.includes(status?.toLowerCase());
};

/**
 * Check if a status is considered "draft" or "inactive"
 * @param {string} status - The status value
 * @returns {boolean} True if status is draft/inactive
 *
 * @example
 * isDraftStatus('draft') // true
 * isDraftStatus('inactive') // true
 */
export const isDraftStatus = (status) => {
  const draftStatuses = ['draft', 'inactive'];
  return draftStatuses.includes(status?.toLowerCase());
};

// ========================================
// Project-specific Status Utilities
// ========================================

/**
 * Get project status color
 * @param {string} status - Project status
 * @returns {string} MUI color variant
 *
 * @example
 * getProjectStatusColor('active') // 'success'
 * getProjectStatusColor('on_hold') // 'warning'
 */
export const getProjectStatusColor = (status) => {
  const projectColors = {
    active: 'success',
    completed: 'primary',
    on_hold: 'warning',
    cancelled: 'error'
  };

  return getStatusColor(status, projectColors);
};

/**
 * Get invoice status color
 * @param {string} status - Invoice status
 * @returns {string} MUI color variant
 *
 * @example
 * getInvoiceStatusColor('draft') // 'default'
 * getInvoiceStatusColor('approved') // 'info'
 */
export const getInvoiceStatusColor = (status) => {
  const invoiceColors = {
    draft: 'default',
    submitted: 'warning',
    pending: 'warning',
    approved: 'info',
    paid: 'success',
    rejected: 'error',
    cancelled: 'error'
  };

  return getStatusColor(status, invoiceColors);
};

/**
 * Get user approval status color
 * @param {string} status - User approval status
 * @returns {string} MUI color variant
 *
 * @example
 * getUserApprovalColor('pending') // 'warning'
 * getUserApprovalColor('approved') // 'success'
 */
export const getUserApprovalColor = (status) => {
  const approvalColors = {
    pending: 'warning',
    approved: 'success',
    rejected: 'error',
    active: 'success',
    inactive: 'success'
  };

  return getStatusColor(status, approvalColors);
};

export default {
  getStatusColor,
  getStatusLabel,
  getInvoiceStatusLabel,
  getRoleColor,
  getRoleLabel,
  isActiveStatus,
  isPendingStatus,
  isNegativeStatus,
  isDraftStatus,
  getProjectStatusColor,
  getInvoiceStatusColor,
  getUserApprovalColor
};
